// GOV-001 P9 — Approval Matrix admin.
//
// Edit the GENERIC engine matrix (approval_rule): who approves each
// action, ₹ slabs, risk, escalation timers. Edits stamp
// admin_modified_at so backend restarts never revert them; rows show
// Seeded vs Customised. Deactivating an action's only tier makes new
// proposals fail closed ("no approval rule configured") — warned here.
import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Alert, Button, Drawer, Form, Input, InputNumber, Popconfirm, Select,
  Space, Switch, Table, Tag, Tooltip, Typography, message,
} from 'antd'
import {
  ControlOutlined, EditOutlined, PlusOutlined, StopOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons'
import dayjs from 'dayjs'
import apiClient from '@/shared/api/client'
import { usePermission } from '@/shared/hooks/usePermission'

const { Text, Title } = Typography

interface Rule {
  id: string
  entity_name: string
  action: string
  tier_order: number
  approver_role_id: string | null
  approver_role: string | null
  amount_min: number | null
  amount_max: number | null
  risk_level: string | null
  escalation_minutes: number | null
  escalation_role_id: string | null
  escalation_role: string | null
  is_active: boolean
  system_seeded: boolean
  admin_modified_at: string | null
  pending_requests: number
}

interface RoleOpt { id: string; role_code: string }

const RISK_COLOR: Record<string, string> = {
  LOW: 'blue', MEDIUM: 'orange', HIGH: 'red',
}

export default function ApprovalMatrixAdminPage() {
  const qc = useQueryClient()
  const canEdit = usePermission('approval_matrix.update')
  const [editRule, setEditRule] = useState<Rule | null>(null)
  const [createOpen, setCreateOpen] = useState(false)
  const [form] = Form.useForm()
  const [createForm] = Form.useForm()

  const rulesQ = useQuery<{ rules: Rule[]; roles: RoleOpt[] }>({
    queryKey: ['approval-matrix-rules'],
    queryFn: () => apiClient.get('/admin/approvals/rules')
      .then((r: any) => r.data),
  })

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ['approval-matrix-rules'] })

  const patchMut = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) =>
      apiClient.patch(`/admin/approvals/rules/${id}`, body),
    onSuccess: () => {
      invalidate(); setEditRule(null)
      message.success('Rule updated — protected from seed resets from now on')
    },
    onError: (e: any) =>
      message.error(e?.response?.data?.detail ?? 'Update failed'),
  })

  const createMut = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      apiClient.post('/admin/approvals/rules', body),
    onSuccess: () => {
      invalidate(); setCreateOpen(false); createForm.resetFields()
      message.success('Rule created')
    },
    onError: (e: any) =>
      message.error(e?.response?.data?.detail ?? 'Create failed'),
  })

  const toggleMut = useMutation({
    mutationFn: (id: string) =>
      apiClient.post(`/admin/approvals/rules/${id}/toggle-active`)
        .then((r: any) => r.data),
    onSuccess: (r: { is_active: boolean }) => {
      invalidate()
      message.success(r.is_active ? 'Rule reactivated' : 'Rule deactivated')
    },
    onError: (e: any) =>
      message.error(e?.response?.data?.detail ?? 'Toggle failed'),
  })

  const roleOptions = useMemo(
    () => (rulesQ.data?.roles ?? []).map(r => ({
      value: r.id, label: r.role_code,
    })),
    [rulesQ.data])

  const openEdit = (r: Rule) => {
    setEditRule(r)
    form.setFieldsValue({
      approver_role_id: r.approver_role_id,
      amount_min: r.amount_min,
      amount_max: r.amount_max,
      risk_level: r.risk_level ?? 'MEDIUM',
      escalation_minutes: r.escalation_minutes,
      escalation_role_id: r.escalation_role_id,
      tier_order: r.tier_order,
    })
  }

  const columns: any[] = [
    {
      title: 'Module', dataIndex: 'entity_name', key: 'entity_name',
      width: 150,
      sorter: (a: Rule, b: Rule) => a.entity_name.localeCompare(b.entity_name),
    },
    { title: 'Action', dataIndex: 'action', key: 'action', width: 220,
      render: (v: string) => <Text code>{v}</Text> },
    { title: 'Tier', dataIndex: 'tier_order', key: 'tier_order', width: 60 },
    {
      title: 'Approver', dataIndex: 'approver_role', key: 'approver_role',
      width: 160,
      render: (v: string | null) => v ? <Tag color="geekblue">{v}</Tag> : '—',
    },
    {
      title: '₹ Band', key: 'band', width: 150,
      render: (_: unknown, r: Rule) => {
        if (r.amount_min == null && r.amount_max == null) {
          return <Text type="secondary">any value</Text>
        }
        const fmt = (n: number | null) =>
          n == null ? '∞' : `₹${n.toLocaleString('en-IN')}`
        return <Text>{fmt(r.amount_min) === '∞' ? '0' : fmt(r.amount_min)} – {fmt(r.amount_max)}</Text>
      },
    },
    {
      title: 'Risk', dataIndex: 'risk_level', key: 'risk_level', width: 90,
      render: (v: string | null) =>
        v ? <Tag color={RISK_COLOR[v]}>{v}</Tag> : '—',
    },
    {
      title: 'Escalation', key: 'esc', width: 180,
      render: (_: unknown, r: Rule) =>
        r.escalation_minutes
          ? <Text>{(r.escalation_minutes / 60).toFixed(r.escalation_minutes % 60 ? 1 : 0)} h → {r.escalation_role ?? '—'}</Text>
          : <Text type="secondary">none</Text>,
    },
    {
      title: 'Source', key: 'source', width: 120,
      render: (_: unknown, r: Rule) => (
        <Space size={4}>
          {r.admin_modified_at
            ? <Tooltip title={`Customised ${dayjs(r.admin_modified_at).format('DD MMM YYYY')} — seed resets skip this rule`}>
                <Tag color="purple">Customised</Tag>
              </Tooltip>
            : r.system_seeded ? <Tag>Seeded</Tag> : <Tag color="cyan">Manual</Tag>}
          {!r.is_active && <Tag color="red">OFF</Tag>}
        </Space>
      ),
    },
    {
      title: 'Pending', dataIndex: 'pending_requests', key: 'pending',
      width: 80,
      render: (v: number) => v > 0 ? <Tag color="processing">{v}</Tag> : '—',
    },
    {
      title: 'Actions', key: 'act', width: 160, fixed: 'right' as const,
      render: (_: unknown, r: Rule) => canEdit ? (
        <Space>
          <Button size="small" icon={<EditOutlined />}
            onClick={() => openEdit(r)}>Edit</Button>
          <Popconfirm
            title={r.is_active ? 'Deactivate this rule?' : 'Reactivate this rule?'}
            description={r.is_active
              ? 'If this is the only tier for the action, new requests will be REFUSED (fail-closed) until a rule is active again.'
              : 'The rule will route new requests again.'}
            onConfirm={() => toggleMut.mutate(r.id)}
          >
            <Button size="small" danger={r.is_active}
              icon={r.is_active ? <StopOutlined /> : <CheckCircleOutlined />} />
          </Popconfirm>
        </Space>
      ) : <Text type="secondary">read-only</Text>,
    },
  ]

  return (
    <div style={{ padding: 16 }}>
      <Space align="center" style={{ marginBottom: 12 }}>
        <ControlOutlined style={{ fontSize: 22 }} />
        <Title level={4} style={{ margin: 0 }}>Approval Matrix</Title>
      </Space>

      <Alert
        type="info" showIcon style={{ marginBottom: 16 }}
        message="This table decides who authorises what"
        description="Changes apply to NEW requests immediately (already-pending requests keep their current assignee until acted on or escalated). Every change is audit-logged. Customised rows survive deployments."
      />

      <Table
        rowKey="id" size="small" loading={rulesQ.isLoading}
        dataSource={rulesQ.data?.rules ?? []} columns={columns}
        pagination={{ pageSize: 25, showSizeChanger: true }}
        scroll={{ x: 1300 }}
        title={() => canEdit ? (
          <Button type="primary" icon={<PlusOutlined />}
            onClick={() => setCreateOpen(true)}>New Rule</Button>
        ) : null}
      />

      {/* Edit drawer */}
      <Drawer
        open={!!editRule} width={420}
        title={editRule ? `Edit — ${editRule.action} (tier ${editRule.tier_order})` : ''}
        onClose={() => setEditRule(null)}
        extra={
          <Button type="primary" loading={patchMut.isPending}
            onClick={() => form.validateFields().then(v => {
              patchMut.mutate({
                id: editRule!.id,
                body: {
                  ...v,
                  clear_amount_min: v.amount_min == null,
                  clear_amount_max: v.amount_max == null,
                  clear_escalation: v.escalation_minutes == null,
                },
              })
            })}>Save</Button>
        }
      >
        <Form form={form} layout="vertical">
          <Form.Item name="approver_role_id" label="Approver Role"
            rules={[{ required: true }]}>
            <Select showSearch optionFilterProp="label"
              options={roleOptions} />
          </Form.Item>
          <Form.Item name="amount_min" label="Amount Min (₹, blank = no lower bound)">
            <InputNumber style={{ width: '100%' }} min={0} />
          </Form.Item>
          <Form.Item name="amount_max" label="Amount Max (₹, blank = no upper bound)">
            <InputNumber style={{ width: '100%' }} min={0} />
          </Form.Item>
          <Form.Item name="risk_level" label="Risk Level">
            <Select options={['LOW', 'MEDIUM', 'HIGH'].map(v => ({ value: v, label: v }))} />
          </Form.Item>
          <Form.Item name="escalation_minutes"
            label="Escalate after (minutes, blank = never)">
            <InputNumber style={{ width: '100%' }} min={5} step={30} />
          </Form.Item>
          <Form.Item name="escalation_role_id" label="Escalate To Role">
            <Select allowClear showSearch optionFilterProp="label"
              options={roleOptions} />
          </Form.Item>
          <Form.Item name="tier_order" label="Tier (1 = first approver)"
            rules={[{ required: true }]}>
            <InputNumber style={{ width: '100%' }} min={1} max={5} />
          </Form.Item>
        </Form>
      </Drawer>

      {/* Create drawer */}
      <Drawer
        open={createOpen} width={420} title="New Approval Rule"
        onClose={() => setCreateOpen(false)}
        extra={
          <Button type="primary" loading={createMut.isPending}
            onClick={() => createForm.validateFields()
              .then(v => createMut.mutate(v))}>Create</Button>
        }
      >
        <Alert type="warning" showIcon style={{ marginBottom: 12 }}
          message="The action code must match what the backend proposes (e.g. po.cancel). A rule with an unknown action simply never fires." />
        <Form form={createForm} layout="vertical"
          initialValues={{ tier_order: 1, risk_level: 'MEDIUM' }}>
          <Form.Item name="entity_name" label="Module / Entity"
            rules={[{ required: true }]}>
            <Input placeholder="e.g. purchase_order" />
          </Form.Item>
          <Form.Item name="action" label="Action Code"
            rules={[{ required: true }]}>
            <Input placeholder="e.g. po.cancel" />
          </Form.Item>
          <Form.Item name="tier_order" label="Tier" rules={[{ required: true }]}>
            <InputNumber style={{ width: '100%' }} min={1} max={5} />
          </Form.Item>
          <Form.Item name="approver_role_id" label="Approver Role"
            rules={[{ required: true }]}>
            <Select showSearch optionFilterProp="label" options={roleOptions} />
          </Form.Item>
          <Form.Item name="amount_min" label="Amount Min (₹, optional)">
            <InputNumber style={{ width: '100%' }} min={0} />
          </Form.Item>
          <Form.Item name="amount_max" label="Amount Max (₹, optional)">
            <InputNumber style={{ width: '100%' }} min={0} />
          </Form.Item>
          <Form.Item name="risk_level" label="Risk Level">
            <Select options={['LOW', 'MEDIUM', 'HIGH'].map(v => ({ value: v, label: v }))} />
          </Form.Item>
          <Form.Item name="escalation_minutes" label="Escalate after (minutes)">
            <InputNumber style={{ width: '100%' }} min={5} step={30} />
          </Form.Item>
          <Form.Item name="escalation_role_id" label="Escalate To Role">
            <Select allowClear showSearch optionFilterProp="label"
              options={roleOptions} />
          </Form.Item>
        </Form>
      </Drawer>
    </div>
  )
}
